import { inject, Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FormattingService } from './formatting.service';

@Injectable({
  providedIn: 'root'
})
export class XmlExportService {

  private readonly formattingService = inject(FormattingService);

  public excelFormatFromLocale() {
    const parts = this.formattingService.formatToPartsCurrency(Number.MAX_VALUE);
  
    const currency = parts.find(p => p.type === 'currency')?.value ?? '';
    const fractionLength = parts.find(p => p.type === 'fraction')?.value.length ?? 0;
  
    const symbolIndex = parts.findIndex(p => p.type === 'currency');
    const integerIndex = parts.findIndex(p => p.type === 'integer');

    const decimals =
      fractionLength > 0 ? '.' + ('0'.repeat(fractionLength)) : '';

    const numberMask = `#,##0${decimals}`;

    return {
      integer: `#,##0`,
      decimal: numberMask,
      currency: symbolIndex < integerIndex
      ? `${currency} ${numberMask}`
      : `${numberMask} ${currency}`
    };
  }

  private applyFormat(
    ws: XLSX.WorkSheet,
    columnHeader: string,
    format: string,
  ): void {

    if (!ws['!ref']) return;
    const range = XLSX.utils.decode_range(ws['!ref']);
  
    let colIndex = -1;
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      if (cell?.v === columnHeader) {
        colIndex = c;
        break;
      }
    }
  
    if (colIndex === -1) return;
  
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const ref = XLSX.utils.encode_cell({ r, c: colIndex });
      const cell = ws[ref];
  
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = format;
      }
    }
  }

  private dateToExcelSerialSafe(date: Date): number {
    const excelEpoch = Date.UTC(1899, 11, 30);
    const dateUtc = Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    );
    const diffMs = (new Date(dateUtc)).getTime() - excelEpoch;

    return Math.round(diffMs / 1000) / 86400;
  }

  public exportJsonToExcel(jsonData: any[], excelFileName: string, columnMap?: any, columnFormat?: Record<string, string>): void {
    const mappedData = columnMap ? jsonData.map(item => {
      const mappedItem = {};
      for (const key in columnMap) {
        if (columnMap.hasOwnProperty(key) && item.hasOwnProperty(key)) {
          mappedItem[columnMap[key]] = item[key];
        }
      }
      return mappedItem;
    }) : [];
  
    let ws: XLSX.WorkSheet;
    const dataToMeasure = (mappedData?.length ? mappedData : jsonData)?.map(item => {
      const newItem = { ...item };
      for (const key of Object.keys(newItem)) {
        if (newItem[key] instanceof Date) {
            newItem[key] = this.dateToExcelSerialSafe(newItem[key]);
        }
      }
      return newItem
    });
  
    ws = XLSX?.utils?.json_to_sheet(dataToMeasure);
  
    const columnWidths = dataToMeasure?.length ? Object.keys(dataToMeasure[0] ?? {})?.map((key, colIdx) => {
      const maxLength = Math.max(
        key?.length,
        ...dataToMeasure.map(item => (item[key] ? String(item[key]).length : 0))
      );
      return { wch: maxLength + 2 };
    }) : [];

    ws['!cols'] = columnWidths;

    if (columnFormat) Object.entries(columnFormat).forEach(([columnHeader, format]) => {
      if (columnHeader && format) this.applyFormat(ws, columnHeader, format)
    })

    const workbook: XLSX.WorkBook = { Sheets: { 'data': ws }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }
  

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`);
  }
}

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

